import * as React from "react";
import { Link, Navigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { Button, Input } from "@jhool-io/fe-components";
import PasswordInput from "../../../components/PasswordInput/PasswordInput";
import AuthWrapper from "../components/AuthWrapper/AuthWrapper";
import useAuthContext from "../../../hooks/useAuthContext";
import useLoginUser from "../hooks/useLoginUser";

interface ILoginProps {
    email: string;
    password: string;
}

const schema = yup.object({
    email: yup
        .string()
        .email("Please enter a valid email")
        .required("Email is required"),
    password: yup.string().required("Password is required"),
});

export default function LoginPage() {
    const { user } = useAuthContext();
    const { loginUser } = useLoginUser();
    const wasAlreadyAuthenticatedRef = React.useRef(Boolean(user));

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<ILoginProps>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    if (user && wasAlreadyAuthenticatedRef.current) {
        const from = localStorage.getItem("from");
        const safeTo =
            from && from.startsWith("/") && !from.startsWith("//") ? from : "/";
        return <Navigate to={safeTo} replace />;
    }

    return (
        <AuthWrapper
            footer={
                <>
                    Don’t have an account yet?
                    <Link
                        to="/verify-identity"
                        className="font-medium text-primary ml-1"
                    >
                        Verify identity
                    </Link>
                </>
            }
        >
            <>
                <h2 className="text-xxl font-bold leading-[32px] mb-4">
                    Welcome back
                </h2>
                <p className="text-[#525866] font-normal text-sm mb-40">
                    Enter your credentials to access your account.
                </p>

                <form className="mt-12" onSubmit={handleSubmit(loginUser)}>
                    <div className="mb-24">
                        <Input
                            {...register("email")}
                            label="Email Address"
                            placeholder="Enter your email address"
                            hasError={!!errors.email}
                            errorText={errors.email?.message}
                        />
                    </div>
                    <PasswordInput
                        {...register("password")}
                        label="Password"
                        placeholder="Enter your password"
                        hasError={!!errors.password}
                        errorText={errors.password?.message}
                    />
                    <Button
                        className="w-full mt-32"
                        type="submit"
                        size="medium"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Logging in..." : "Login"}
                    </Button>
                </form>
                <div className="text-center pt-[12px]">
                    <Link
                        to="/reset-password"
                        className="text-sm font-medium text-primary"
                    >
                        Forgot password?
                    </Link>
                </div>
            </>
        </AuthWrapper>
    );
}
